// ===========================================
// Age Groups Management Page
// ===========================================
// CRUD interface for managing age groups with drag-and-drop reordering

import { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Typography,
  Skeleton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PageHeader from '../../components/common/PageHeader';
import FormModal from '../../components/common/FormModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import ColorPickerField from '../../components/common/ColorPickerField';
import {
  useAgeGroups,
  useCreateAgeGroup,
  useUpdateAgeGroup,
  useDeleteAgeGroup,
  useReorderAgeGroups,
} from '../../hooks/useAdmin';
import { AgeGroup } from '../../api/admin.api';

// ===========================================
// SORTABLE ITEM COMPONENT
// ===========================================

interface SortableAgeGroupItemProps {
  ageGroup: AgeGroup;
  onEdit: (ageGroup: AgeGroup) => void;
  onDelete: (ageGroup: AgeGroup) => void;
}

function SortableAgeGroupItem({ ageGroup, onEdit, onDelete }: SortableAgeGroupItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ageGroup.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const canDelete = !ageGroup.isActive;

  // Format age range for display
  const getAgeRange = () => {
    if (ageGroup.minAge !== null && ageGroup.maxAge !== null) {
      return `${ageGroup.minAge}-${ageGroup.maxAge} years`;
    } else if (ageGroup.minAge !== null) {
      return `${ageGroup.minAge}+ years`;
    } else if (ageGroup.maxAge !== null) {
      return `Up to ${ageGroup.maxAge} years`;
    }
    return 'No age range set';
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          mr: 2,
          color: 'text.secondary',
          '&:active': {
            cursor: 'grabbing',
          },
        }}
      >
        <DragIcon />
      </Box>
      {ageGroup.color && (
        <Box
          sx={{
            width: 24,
            height: 24,
            bgcolor: ageGroup.color,
            borderRadius: 0.5,
            border: '1px solid',
            borderColor: 'divider',
            mr: 1.5,
            flexShrink: 0,
          }}
        />
      )}
      <ListItemText
        primary={ageGroup.name}
        secondary={getAgeRange()}
        sx={{ flex: 1 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {ageGroup.isActive ? (
          <Chip label="Active" size="small" color="success" />
        ) : (
          <Chip label="Inactive" size="small" color="default" />
        )}
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(ageGroup)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={canDelete ? 'Delete' : 'Set status to Inactive to delete this age group'}>
            <span>
              <IconButton
                size="small"
                onClick={() => canDelete && onDelete(ageGroup)}
                color={canDelete ? 'error' : 'default'}
                disabled={!canDelete}
                sx={{
                  opacity: canDelete ? 1 : 0.5,
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </ListItem>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function AgeGroupsPage() {
  const { data: ageGroupsData, isLoading, error } = useAgeGroups();
  const createMutation = useCreateAgeGroup();
  const updateMutation = useUpdateAgeGroup();
  const deleteMutation = useDeleteAgeGroup();
  const reorderMutation = useReorderAgeGroups();

  // Get age groups array from response
  const ageGroups = ageGroupsData ?? [];

  // Filter state
  const [hideInactive, setHideInactive] = useState(false);

  // Form state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAgeGroup, setEditingAgeGroup] = useState<AgeGroup | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    minAge: '',
    maxAge: '',
    isActive: true,
    color: null as string | null,
  });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ageGroupToDelete, setAgeGroupToDelete] = useState<AgeGroup | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Filter age groups
  const filteredAgeGroups = useMemo((): AgeGroup[] => {
    if (hideInactive) {
      return ageGroups.filter((ageGroup: AgeGroup) => ageGroup.isActive);
    }
    return ageGroups;
  }, [ageGroups, hideInactive]);

  // Handlers
  const handleAdd = () => {
    setEditingAgeGroup(null);
    setFormData({ name: '', minAge: '', maxAge: '', isActive: true, color: null });
    setModalOpen(true);
  };

  const handleEdit = (ageGroup: AgeGroup) => {
    setEditingAgeGroup(ageGroup);
    setFormData({
      name: ageGroup.name,
      minAge: ageGroup.minAge !== null ? String(ageGroup.minAge) : '',
      maxAge: ageGroup.maxAge !== null ? String(ageGroup.maxAge) : '',
      isActive: ageGroup.isActive,
      color: ageGroup.color,
    });
    setModalOpen(true);
  };

  const handleDelete = (ageGroup: AgeGroup) => {
    setAgeGroupToDelete(ageGroup);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        name: formData.name,
        minAge: formData.minAge ? parseInt(formData.minAge, 10) : undefined,
        maxAge: formData.maxAge ? parseInt(formData.maxAge, 10) : undefined,
        isActive: formData.isActive,
        color: formData.color ?? undefined,
      };

      if (editingAgeGroup) {
        await updateMutation.mutateAsync({
          id: editingAgeGroup.id,
          data,
        });
      } else {
        await createMutation.mutateAsync(data);
      }
      setModalOpen(false);
    } catch {
      // Error is handled by mutation
    }
  };

  const handleConfirmDelete = async () => {
    if (ageGroupToDelete) {
      try {
        await deleteMutation.mutateAsync(ageGroupToDelete.id);
        setDeleteDialogOpen(false);
        setAgeGroupToDelete(null);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Reorder the full list, not just filtered
      const reorderedIds = arrayMove(
        ageGroups.map((i: AgeGroup) => i.id),
        ageGroups.findIndex((i: AgeGroup) => i.id === active.id),
        ageGroups.findIndex((i: AgeGroup) => i.id === over.id)
      );

      // Call the reorder API
      try {
        await reorderMutation.mutateAsync(reorderedIds);
      } catch {
        // Error is handled by mutation
      }
    }
  };

  const isFormLoading = createMutation.isPending || updateMutation.isPending;
  const isDeleteLoading = deleteMutation.isPending;

  return (
    <Box>
      <PageHeader
        title="Age Groups"
        subtitle="Manage student age groups. Drag to reorder how age groups appear throughout the system."
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'Age Groups' },
        ]}
        actionLabel="Add Age Group"
        onAction={handleAdd}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load age groups. Please try again.
        </Alert>
      )}

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2 }}
        alignItems="center"
      >
        <FormControlLabel
          control={
            <Switch
              checked={hideInactive}
              onChange={(e) => setHideInactive(e.target.checked)}
              size="small"
            />
          }
          label="Hide inactive age groups"
        />

        {filteredAgeGroups.length !== ageGroups.length && (
          <Chip
            label={`Showing ${filteredAgeGroups.length} of ${ageGroups.length} age groups`}
            size="small"
            variant="outlined"
          />
        )}
      </Stack>

      {/* Age Groups List */}
      <Paper sx={{ overflow: 'hidden' }}>
        {isLoading ? (
          <List>
            {[1, 2, 3, 4].map((i) => (
              <ListItem key={i} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ mr: 2 }}>
                  <Skeleton variant="rectangular" width={24} height={24} />
                </Box>
                <ListItemText
                  primary={<Skeleton variant="text" width="60%" />}
                  secondary={<Skeleton variant="text" width="30%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : filteredAgeGroups.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {ageGroups.length > 0
                ? 'No age groups match the current filter.'
                : "No age groups configured. Click 'Add Age Group' to create one."}
            </Typography>
          </Box>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredAgeGroups.map((i: AgeGroup) => i.id)}
              strategy={verticalListSortingStrategy}
            >
              <List disablePadding>
                {filteredAgeGroups.map((ageGroup: AgeGroup) => (
                  <SortableAgeGroupItem
                    key={ageGroup.id}
                    ageGroup={ageGroup}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        )}
      </Paper>

      {/* Add/Edit Modal */}
      <FormModal
        open={modalOpen}
        title={editingAgeGroup ? 'Edit Age Group' : 'Add Age Group'}
        submitLabel={editingAgeGroup ? 'Update' : 'Create'}
        loading={isFormLoading}
        onSubmit={handleSubmit}
        onClose={() => setModalOpen(false)}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Age Group Name"
            placeholder="e.g., Pre-school"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            fullWidth
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Minimum Age"
              type="number"
              placeholder="e.g., 3"
              value={formData.minAge}
              onChange={(e) => setFormData({ ...formData, minAge: e.target.value })}
              fullWidth
              inputProps={{ min: 0, max: 100 }}
              helperText="Optional"
            />
            <TextField
              label="Maximum Age"
              type="number"
              placeholder="e.g., 5"
              value={formData.maxAge}
              onChange={(e) => setFormData({ ...formData, maxAge: e.target.value })}
              fullWidth
              inputProps={{ min: 0, max: 100 }}
              helperText="Optional"
            />
          </Box>
          <ColorPickerField
            label="Color"
            value={formData.color}
            onChange={(color) => setFormData({ ...formData, color })}
            helperText="Optional color for visual identification (matches character mascots)"
            useSchoolPalette
          />
          {editingAgeGroup && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label={formData.isActive ? 'Active' : 'Inactive'}
            />
          )}
        </Box>
      </FormModal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Age Group"
        message={`Are you sure you want to delete "${ageGroupToDelete?.name}"? Students assigned to this age group will need to be reassigned.`}
        confirmLabel="Delete"
        confirmColor="error"
        loading={isDeleteLoading}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
